/**
 * Joignabilité d'une machine, et façon de nommer ce qu'on ne sait pas.
 *
 * Pourquoi ce fichier existe : publier un ordre a toujours l'air de réussir. Une machine
 * éteinte n'est pas connectée, son message est simplement jeté. Le seul indice fiable est
 * la dernière trame reçue — le backend la résume en deux états visibles, plus un cas de
 * repli, et tout l'écran doit parler d'eux avec les mêmes mots. Deux composants qui
 * inventent chacun leur libellé, c'est exactement ce qui a laissé croire pendant deux
 * heures qu'une consigne était suivie.
 *
 * Le troisième état a changé de sens : la date du dernier contact est désormais lue en
 * base et non plus dans la mémoire du serveur, donc une machine connue reste
 * « déconnectée depuis 1 h 07 min » après un redémarrage au lieu de repartir à zéro.
 * Il ne reste que le cas d'une machine dont aucun contact n'est enregistré.
 *
 * Vocabulaire à l'écran : on dit ce que l'utilisateur constate (« déconnectée depuis
 * 12 min »), jamais comment on l'a su. Aucun terme réseau, aucun paramètre de connexion.
 */

export const CONNECTEE = 'connectee';
export const DECONNECTEE = 'deconnectee';
export const SANS_CONTACT = 'sans_contact';

/**
 * Ramène un actif — ou son bloc `monitoring` — à l'un des états.
 *
 * `etat_connexion` est la source précise et prioritaire. `statut_connexion` et
 * `mqtt_status` sont les anciens champs à deux valeurs, gardés pour les types d'actifs
 * qui n'ont pas encore de relevé de fraîcheur : ils ne savent pas distinguer « plus de
 * nouvelles » de « aucun contact enregistré », donc on ne s'en sert qu'en dernier recours.
 *
 * @returns {string|null} un des états connus, ou null si on ne sait rien du tout
 */
export function etatJoignabilite(source) {
  if (!source) return null;
  const precis = source.etat_connexion || source.monitoring?.etat_connexion;
  if (precis) return precis;
  const ancien = source.statut_connexion || source.mqtt_status
    || source.monitoring?.mqtt_status;
  if (ancien === 'connecte' || ancien === CONNECTEE) return CONNECTEE;
  if (ancien === 'deconnecte' || ancien === DECONNECTEE) return DECONNECTEE;
  return null;
}

/** Ancienneté de la dernière trame, prise là où elle se trouve. */
export function ageJoignabilite(source) {
  if (!source) return null;
  const age = source.age_s ?? source.monitoring?.age_s;
  return age ?? null;
}

/** Horodatage de la dernière trame, pris là où il se trouve. */
export function vuA(source) {
  if (!source) return null;
  return source.vu_a ?? source.monitoring?.vu_a ?? null;
}

/** Les chiffres affichés sont-ils les derniers connus, et vieux ? */
export function donneesPerimees(source) {
  if (!source) return false;
  return !!(source.donnees_perimees ?? source.monitoring?.donnees_perimees);
}

/**
 * Durée lisible par un humain pressé, dans le format arrêté avec le client :
 *
 *   moins d'une minute → « moins d'une minute »
 *   moins d'une heure  → « 14 min »
 *   une heure et plus  → « 1 h 07 min »   (minutes toujours sur deux chiffres)
 *   un jour et plus    → « 3 j 04 h »
 *
 * Les secondes ne sont jamais affichées : personne ne pilote à la seconde près, et
 * « 40 s » se lit comme une précision qu'on n'a pas. Les deux chiffres sont tenus même
 * quand le reste vaut zéro (« 2 h 00 min »), pour que la durée garde toujours la même
 * forme d'un rafraîchissement à l'autre.
 */
export function ancienneteTexte(ageS) {
  if (ageS == null) return null;
  const s = Math.max(0, Math.round(ageS));
  const min = Math.floor(s / 60);
  if (min < 1) return "moins d'une minute";
  if (min < 60) return `${min} min`;
  const heures = Math.floor(min / 60);
  if (heures < 24) return `${heures} h ${String(min % 60).padStart(2, '0')} min`;
  const jours = Math.floor(heures / 24);
  return `${jours} j ${String(heures % 24).padStart(2, '0')} h`;
}

/** Date et heure courtes, en français. */
export function horodatageTexte(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return null; }
}

/**
 * Libellé de la pastille. `genre` accorde l'adjectif : 'f' pour une batterie,
 * 'm' pour une PAC ou un ballon — l'écran de pilotage mélange les deux.
 *
 * « Aucun contact enregistré » ne s'accorde pas : c'est le contact qui manque, pas la
 * machine qui est dans un état. Un seul libellé suffit donc pour tous les types.
 */
export function libelleJoignabilite(etat, ageS, genre = 'f') {
  const e = genre === 'f' ? 'e' : '';
  if (etat === CONNECTEE) return `Connecté${e}`;
  if (etat === DECONNECTEE) {
    const age = ancienneteTexte(ageS);
    return age ? `Déconnecté${e} depuis ${age}` : `Déconnecté${e}`;
  }
  if (etat === SANS_CONTACT) return 'Aucun contact enregistré';
  return 'État inconnu';
}

/** Explication au survol — dit ce que l'état implique, pas comment on l'a mesuré. */
export function titreJoignabilite(etat, ageS) {
  if (etat === CONNECTEE) {
    return "La machine envoie ses mesures : elle est sous tension et reçoit les consignes.";
  }
  if (etat === DECONNECTEE) {
    const age = ancienneteTexte(ageS);
    return `Plus aucune mesure reçue${age ? ` depuis ${age}` : ''}. `
      + "Les chiffres affichés sont les derniers connus, et une consigne envoyée "
      + "maintenant ne serait pas reçue.";
  }
  if (etat === SANS_CONTACT) {
    return "Aucun contact n'est enregistré pour cette machine : elle n'a encore transmis "
      + "aucune mesure, et rien ne dit qu'elle est en service.";
  }
  return "On ne sait pas si cette machine est joignable.";
}

/**
 * Témoin du panneau de pilotage. Vocabulaire de David : « est-ce que j'ai un retour
 * comme quoi savoir si la machine est bien allumée ? ». On parle donc de la machine,
 * pas du réseau.
 */
export function libelleMachine(etat, ageS) {
  if (etat === CONNECTEE) return 'Machine allumée';
  if (etat === DECONNECTEE) {
    const age = ancienneteTexte(ageS);
    return age ? `Machine injoignable depuis ${age}` : 'Machine injoignable';
  }
  if (etat === SANS_CONTACT) return 'Machine sans contact enregistré';
  return 'État de la machine inconnu';
}

/** Une machine joignable est la seule à qui il sert de parler. */
export function estJoignable(etat) {
  return etat === CONNECTEE;
}

/**
 * Une valeur absente s'affiche « inconnu », jamais 0 : un SoC à 0 % et un SoC qu'on
 * ignore appellent deux décisions opposées.
 */
export function mesureTexte(valeur, unite = '') {
  if (valeur == null || valeur === '') return 'inconnu';
  return unite ? `${valeur} ${unite}` : String(valeur);
}

/** Verdict de la consigne que Flexcore republie en boucle. */
export const VERDICT_CONSIGNE = {
  suivie: {
    libelle: 'Consigne suivie par la batterie',
    detail: "Les mesures reçues correspondent à la consigne envoyée.",
    ton: 'ok',
  },
  non_confirmee: {
    libelle: 'Pas encore confirmée par la batterie',
    detail: "La batterie répond mais n'applique pas encore la consigne. "
      + "Le matériel peut mettre plusieurs minutes, et une charge n'aboutit que s'il y a "
      + 'de quoi charger.',
    ton: 'attente',
  },
  device_injoignable: {
    libelle: 'Batterie injoignable : consigne non reçue',
    detail: "La consigne continue de partir, mais la batterie n'envoie plus rien : rien "
      + "ne dit qu'elle l'entend. À vérifier auprès du client : machine éteinte ou coupée.",
    ton: 'alerte',
  },
};

/* ====================================================================================
   CENTRALES PHOTOVOLTAIQUES — dire la verite sur l'age d'une production (07/09/2026)
   ====================================================================================

   Le defaut repare ici : les trois ecrans qui affichent du PV ecrivaient
   `${m.production_kw || 0} kW`. Une absence de mesure s'y lisait donc « 0 kW », soit
   « la centrale ne produit rien » au lieu de « on ne mesure pas ». C'est mot pour mot le
   mensonge que `core/verite_materiel` a ete ecrit pour tuer cote serveur, et il survivait
   cote ecran — le serveur rendait bien `null`, le front le peignait en zero.

   Le second defaut est plus subtil, et c'est celui qui justifie deux fonctions plutot
   qu'un `mesureTexte` de plus. L'API S-Miles ne rend AUCUNE puissance instantanee : elle
   rend une serie au pas de quinze minutes. Ce que l'ecran appelle « Production » est le
   dernier point de cette serie, et a 23 h ce dernier point date du crepuscule.

   D'ou la separation :

       productionCouranteTexte   la production MAINTENANT, et « inconnu » des que le
                                 serveur n'a pas pu PROUVER la fraicheur du point
       dernierPointPvTexte       le dernier point connu, toujours accompagne de son
                                 heure — utilisable la nuit, parce qu'il ne pretend rien

   Les deux viennent du meme relevé. La difference n'est pas dans la donnee, elle est dans
   ce qu'on a le droit d'en affirmer.
*/

/** La production courante, ou 'inconnu' tant que sa fraicheur n'est pas demontree. */
export function productionCouranteTexte(m) {
  if (!m) return 'inconnu';
  // `puissance_courante_prouvee` est calcule par le serveur (`core/releve_pv`), qui seul
  // connait le fuseau de la centrale. Le recalculer ici serait impossible : l'horodatage
  // arrive en heure locale de la centrale, sans decalage.
  if (!m.puissance_courante_prouvee) return 'inconnu';
  return mesureTexte(m.production_kw ?? m.puissance_instantanee_kw, 'kW');
}

/** Le dernier point connu avec son heure, ou null s'il n'y en a aucun. */
export function dernierPointPvTexte(m) {
  if (!m || m.puissance_derniere_w == null) return null;
  const watts = Math.round(m.puissance_derniere_w);
  const quand = m.puissance_derniere_instant;
  if (!quand) return `${watts} W`;
  // On affiche l'horodatage TEL QUE la centrale l'ecrit, sans le convertir. Le convertir
  // demanderait son fuseau, et se tromper de fuseau deplacerait un point de deux heures
  // sans que rien ne le signale. Brut, il reste verifiable dans S-Miles.
  const heure = String(quand).slice(11, 16) || String(quand);
  return `${watts} W à ${heure}`;
}

/** Le motif pour lequel une centrale n'affiche rien, en une phrase. null s'il n'y en a pas.
 *
 *  Un ecran vide DOIT pouvoir dire pourquoi il est vide : « cle absente » appelle un
 *  geste de configuration, « plafond atteint » appelle de la patience, et « pas encore de
 *  mesure » n'appelle rien du tout. Les confondre dans un tiret muet, c'est la panne
 *  silencieuse transposee a l'interface.
 */
export function motifPvTexte(m) {
  if (!m || !m.motifs) return null;
  const motifs = Object.values(m.motifs).filter(Boolean);
  if (!motifs.length) return null;
  const explications = {
    cle_absente: "aucune clé API S-Miles n'est posée sur cette instance",
    cle_refusee: 'la clé S-Miles a été refusée par Hoymiles',
    plafond_atteint: "le plafond d'appels S-Miles est atteint, la mesure reprendra seule",
  };
  const premier = motifs[0];
  return explications[premier] || String(premier);
}

/** L'etat de l'onduleur en francais. 'inconnu' quand il n'a pas pu etre lu. */
export const ETAT_ONDULEUR = {
  nominal: { libelle: 'Nominal', ton: 'ok' },
  hors_ligne: { libelle: 'Hors ligne', ton: 'ko' },
  alarme: { libelle: 'Alarme', ton: 'alerte' },
};

export function etatOnduleurTexte(m) {
  const etat = m && m.etat_onduleur;
  return etat && ETAT_ONDULEUR[etat] ? ETAT_ONDULEUR[etat].libelle : 'inconnu';
}
